import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';

import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import AppNavbar from '../components/AppNavbar';
import AssetRunCard from '../components/AssetRunCard';
import ClassificationBanner from '../components/ClassificationBanner';
import Header from '../components/Header';
import SideMenu from '../components/SideMenu';
import AppTheme from '../shared-theme/AppTheme';
import axiosInstance from '../utils/axiosInstance';
import { isRunActive } from '../utils/assetRuns';
import { formatCapabilityLabel } from '../utils/capabilityContract';

import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from './theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

const WORKSPACE_AVAILABLE_STATUSES = new Set([
  'accepted',
  'in_progress',
  'waiting',
  'awaiting_resource',
  'completed',
  'failed',
]);

function formatTimestamp(value) {
  if (!value) {
    return 'N/A';
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

function statusLabel(value) {
  if (value === 'pending_provider_response') {
    return 'Awaiting Response';
  }
  return formatCapabilityLabel(value || 'unknown');
}

function statusColor(value) {
  if (value === 'accepted') {
    return 'success';
  }
  if (value === 'declined') {
    return 'error';
  }
  return 'warning';
}

function businessUnitLabel(unit) {
  const parent = unit?.parent_partner?.display_name || unit?.parent_partner?.partner_code;
  const unitName = unit?.display_name || unit?.business_unit_code || unit?.business_unit_id;
  return parent ? `${parent} / ${unitName}` : unitName;
}

function fieldName(field) {
  return field?.name || field?.key || '';
}

function fieldLabel(field, fallback) {
  return field?.display_name || field?.label || formatCapabilityLabel(fallback);
}

function fieldType(field) {
  return String(field?.type || 'text').toLowerCase();
}

function renderScalar(value) {
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (Array.isArray(value)) {
    return value.map(renderScalar).join(', ');
  }
  if (value && typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }
  return String(value ?? 'N/A');
}

function artifactsForField(artifacts, source, fieldKey, value) {
  const ids = new Set((Array.isArray(value) ? value : [value]).map((item) => String(item || '')).filter(Boolean));
  return (artifacts || []).filter((artifact) => (
    artifact.source === source &&
    artifact.field_key === fieldKey &&
    (!ids.size || ids.has(artifact.file_id) || ids.has(artifact.artifact_id))
  ));
}

function ValueTable({ title, fields, values, artifacts, source }) {
  const rows = useMemo(() => {
    const fieldsByKey = new Map((fields || []).map((field) => [fieldName(field), field]));
    return Object.entries(values || {}).map(([key, value]) => ({
      key,
      field: fieldsByKey.get(key),
      value,
    }));
  }, [fields, values]);

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Box sx={{ px: 2, py: 1.25, bgcolor: 'background.default' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{title}</Typography>
      </Box>
      <Table size="small">
        <TableBody>
          {rows.length ? rows.map((row) => {
            const type = fieldType(row.field);
            const fieldArtifacts = type === 'file'
              ? artifactsForField(artifacts, source, row.key, row.value)
              : [];
            return (
              <TableRow key={row.key}>
                <TableCell sx={{ width: 280, fontWeight: 700 }}>
                  {fieldLabel(row.field, row.key)}
                </TableCell>
                <TableCell sx={{ whiteSpace: type === 'file' ? 'normal' : 'pre-wrap', wordBreak: 'break-word' }}>
                  {type === 'file' && fieldArtifacts.length
                    ? fieldArtifacts.map((artifact) => artifact.filename || artifact.file_name || artifact.file_id).join(', ')
                    : renderScalar(row.value)}
                </TableCell>
              </TableRow>
            );
          }) : (
            <TableRow>
              <TableCell>
                <Typography variant="body2" color="text.secondary">No values submitted.</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );
}

function OutputsTable({ outputs }) {
  const normalized = Array.isArray(outputs) ? outputs : [];
  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Box sx={{ px: 2, py: 1.25, bgcolor: 'background.default' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Expected Outputs</Typography>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Output</TableCell>
            <TableCell>Kind</TableCell>
            <TableCell>Requirement</TableCell>
            <TableCell>Result</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {normalized.length ? normalized.map((output) => (
            <TableRow key={output.output_id || output.key}>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{output.name || formatCapabilityLabel(output.key)}</Typography>
                {output.description ? (
                  <Typography variant="caption" color="text.secondary">{output.description}</Typography>
                ) : null}
              </TableCell>
              <TableCell>{formatCapabilityLabel(output.kind)}</TableCell>
              <TableCell>{output.required ? 'Required' : 'Optional'}</TableCell>
              <TableCell sx={{ minWidth: 260 }}>
                {String(output.kind || '').toLowerCase() === 'asset_run' && output.fulfillment ? (
                  <Stack spacing={1}>
                    {(output.fulfillment.runs || []).map((run) => (
                      <AssetRunCard key={run.run_id} run={run} compact />
                    ))}
                    {!(output.fulfillment.runs || []).length ? (
                      <Typography variant="body2" color="text.secondary">Selected Run details are unavailable.</Typography>
                    ) : null}
                  </Stack>
                ) : output.fulfillment ? (
                  <Chip size="small" color="success" label="Provided" />
                ) : (
                  <Chip size="small" variant="outlined" label="Not Provided" />
                )}
              </TableCell>
            </TableRow>
          )) : (
            <TableRow>
              <TableCell colSpan={4}>
                <Typography variant="body2" color="text.secondary">No expected outputs.</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );
}

export default function DirectRequestDetailPage(props) {
  const { requestCapabilityId } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionBusy, setActionBusy] = useState('');
  const [responseMessage, setResponseMessage] = useState('');
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');

  const loadDetail = useCallback(async ({ background = false } = {}) => {
    if (!background) {
      setLoading(true);
      setError('');
    }
    try {
      const response = await axiosInstance.get(`/requests/direct/${encodeURIComponent(requestCapabilityId)}`);
      setDetail(response.data || null);
      setResponseMessage(response.data?.request_capability?.provider_response_message || '');
    } catch (err) {
      if (!background) {
        setError(err?.response?.data?.message || err?.message || 'Unable to load request.');
      }
    } finally {
      if (!background) {
        setLoading(false);
      }
    }
  }, [requestCapabilityId]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const respond = async (action) => {
    setActionBusy(action);
    setError('');
    setFeedback('');
    try {
      const response = await axiosInstance.post(
        `/requests/direct/${encodeURIComponent(requestCapabilityId)}/${action}`,
        { provider_response_message: responseMessage.trim() || undefined },
      );
      setDetail(response.data || null);
      setFeedback(action === 'accept' ? 'Request accepted.' : 'Request declined.');
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || `Unable to ${action} request.`);
    } finally {
      setActionBusy('');
    }
  };

  const request = detail?.request || {};
  const requestCapability = detail?.request_capability || {};
  const snapshot = requestCapability.capability_snapshot || {};
  const authorization = detail?.authorization || {};
  const runs = Array.isArray(detail?.runs)
    ? detail.runs
    : Array.isArray(requestCapability.runs) ? requestCapability.runs : [];
  const expectedOutputs = requestCapability.expected_outputs
    || detail?.expected_outputs
    || snapshot.expected_outputs
    || detail?.capability?.outputs
    || [];
  const hasActiveRuns = runs.some(isRunActive);
  const workspaceId = detail?.workspace_id;
  const workspaceHref = `/workspaces/${workspaceId}`;
  const isCui = Boolean(request.is_cui ?? request.metadata?.is_cui);

  useEffect(() => {
    if (!hasActiveRuns) {
      return undefined;
    }
    const timer = window.setInterval(() => loadDetail({ background: true }), 15000);
    return () => window.clearInterval(timer);
  }, [hasActiveRuns, loadDetail]);

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Header
            breadcrumbItems={[
              { label: 'AMX xConsole', href: '/' },
              { label: 'Requests', href: '/requests' },
              { label: request.title || 'Direct Request' },
            ]}
          />

          {loading ? (
            <Stack direction="row" spacing={2} alignItems="center">
              <CircularProgress size={24} />
              <Typography>Loading request...</Typography>
            </Stack>
          ) : error && !detail ? (
            <Alert severity="error">{error}</Alert>
          ) : detail ? (
            <Stack spacing={3} sx={{ maxWidth: 1100 }}>
              <ClassificationBanner isCui={isCui} />
              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>
                    {requestCapability.capability_snapshot?.capability_name || detail.capability?.capability_name || 'Capability Request'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {request.title}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip color={statusColor(requestCapability.status)} label={statusLabel(requestCapability.status)} />
                  <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate('/requests')}>
                    Requests
                  </Button>
                </Stack>
              </Stack>

              {feedback ? <Alert severity="success">{feedback}</Alert> : null}
              {error ? <Alert severity="error">{error}</Alert> : null}

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.25}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Request Summary</Typography>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} divider={<Divider orientation="vertical" flexItem />}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary">Requested By</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{businessUnitLabel(detail.requesting_business_unit)}</Typography>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary">Provider</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{businessUnitLabel(detail.provider_business_unit)}</Typography>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary">Submitted</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatTimestamp(request.created_at)}</Typography>
                    </Box>
                  </Stack>
                  {request.description ? (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{request.description}</Typography>
                  ) : null}
                  {requestCapability.provider_response_message ? (
                    <Alert severity={requestCapability.status === 'declined' ? 'warning' : 'info'}>
                      {requestCapability.provider_response_message}
                    </Alert>
                  ) : null}
                </Stack>
              </Paper>

              <ValueTable
                title="Shared Need Information"
                fields={snapshot.shared_fields || []}
                values={requestCapability.shared_request_values}
                artifacts={detail.artifacts}
                source="shared"
              />
              <ValueTable
                title="Provider-Specific Information"
                fields={snapshot.additional_fields || []}
                values={requestCapability.capability_request_values}
                artifacts={detail.artifacts}
                source="capability"
              />
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.25 }}>Asset Runs</Typography>
                <Stack spacing={1}>
                  {runs.length ? runs.map((run) => (
                    <AssetRunCard key={run.run_id} run={run} compact />
                  )) : (
                    <Typography variant="body2" color="text.secondary">
                      No Asset Runs are associated with this Capability Request yet.
                    </Typography>
                  )}
                </Stack>
              </Paper>
              <OutputsTable outputs={expectedOutputs} />

              {authorization.can_accept || authorization.can_decline ? (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={2}>
                    <TextField
                      label="Response Message / Notes"
                      value={responseMessage}
                      onChange={(event) => setResponseMessage(event.target.value)}
                      multiline
                      minRows={3}
                      fullWidth
                      disabled={Boolean(actionBusy)}
                    />
                    <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                      <Button
                        color="error"
                        variant="outlined"
                        startIcon={<CancelRoundedIcon />}
                        disabled={Boolean(actionBusy)}
                        onClick={() => respond('decline')}
                      >
                        {actionBusy === 'decline' ? 'Declining...' : 'Decline'}
                      </Button>
                      <Button
                        color="success"
                        variant="contained"
                        startIcon={<CheckCircleRoundedIcon />}
                        disabled={Boolean(actionBusy)}
                        onClick={() => respond('accept')}
                      >
                        {actionBusy === 'accept' ? 'Accepting...' : 'Accept'}
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
              ) : null}

              {workspaceId && WORKSPACE_AVAILABLE_STATUSES.has(requestCapability.status) ? (
                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    component={RouterLink}
                    to={workspaceHref}
                    variant="contained"
                    startIcon={<OpenInNewRoundedIcon />}
                  >
                    Open Workspace
                  </Button>
                </Stack>
              ) : null}
            </Stack>
          ) : (
            <Alert severity="error">Request not found.</Alert>
          )}
        </Box>
      </Box>
    </AppTheme>
  );
}
